/**
 * Linear inequalities and solution sets over exact rationals.
 *
 * Mirrors the LinearEquation model but tracks direction, including the rule
 * that dividing or multiplying by a negative reverses the inequality. Solving
 * is done by an independent routine (isolate the variable, flipping when the
 * final coefficient is negative), and a SolutionSet is verified by membership
 * probes so a claimed set can be checked separately from how it was built.
 */

import { Rational } from "./rational";
import { LinearExpr } from "./linear";

export type Op = "<" | "<=" | ">" | ">=";

export function flip(op: Op): Op {
  switch (op) {
    case "<":
      return ">";
    case "<=":
      return ">=";
    case ">":
      return "<";
    case ">=":
      return "<=";
  }
}

export function isStrict(op: Op): boolean {
  return op === "<" || op === ">";
}

/** A single linear inequality: left OP right. */
export class LinearInequality {
  constructor(
    readonly left: LinearExpr,
    readonly op: Op,
    readonly right: LinearExpr,
  ) {}

  /** Does a value satisfy the inequality? Independent of solve(). */
  isSatisfiedBy(x: Rational): boolean {
    const l = this.left.evaluate(x);
    const r = this.right.evaluate(x);
    const c = l.compare(r);
    switch (this.op) {
      case "<":
        return c < 0;
      case "<=":
        return c <= 0;
      case ">":
        return c > 0;
      case ">=":
        return c >= 0;
    }
  }

  /**
   * Solve into a boundary + direction, applying the flip rule. Returns either a
   * bounded solution (x OP value) or an "all"/"none" degenerate result when the
   * variable cancels.
   */
  solve():
    | { kind: "ray"; op: Op; value: Rational }
    | { kind: "all" }
    | { kind: "none" } {
    // Move to (a) x  OP  b, where a = leftCoeff - rightCoeff.
    const a = this.left.coeff.sub(this.right.coeff);
    const b = this.right.constant.sub(this.left.constant);
    if (a.isZero()) {
      // 0 OP b  ->  constant comparison; independent of x.
      const holds = this.compareConst(b);
      return holds ? { kind: "all" } : { kind: "none" };
    }
    // x  (op')  b/a, flipping op' if a < 0.
    const value = b.div(a);
    const op = a.isNegative() ? flip(this.op) : this.op;
    return { kind: "ray", op, value };
  }

  /** Whether "0 OP b" holds (variable cancelled). */
  private compareConst(b: Rational): boolean {
    // Original form after moving: a*x + (leftConst) OP rightConst with a=0 =>
    // leftConst OP rightConst, i.e. 0 OP (rightConst - leftConst) = b.
    const c = Rational.ZERO.compare(b);
    switch (this.op) {
      case "<":
        return c < 0;
      case "<=":
        return c <= 0;
      case ">":
        return c > 0;
      case ">=":
        return c >= 0;
    }
  }
}

/** A solution set over the reals for a single variable. */
export type SolutionSet =
  | { kind: "all" }
  | { kind: "empty" }
  | { kind: "ray"; op: Op; value: Rational }
  | {
      // AND: bounded interval lo (opLo) x (opHi) hi.
      kind: "interval";
      lo: Rational;
      loInclusive: boolean;
      hi: Rational;
      hiInclusive: boolean;
    }
  | {
      // OR: union of two outward rays x <(=) lo  OR  x >(=) hi.
      kind: "union";
      lo: Rational;
      loInclusive: boolean;
      hi: Rational;
      hiInclusive: boolean;
    };

/** Membership test used to independently verify a claimed solution set. */
export function inSolutionSet(set: SolutionSet, x: Rational): boolean {
  switch (set.kind) {
    case "all":
      return true;
    case "empty":
      return false;
    case "ray": {
      const c = x.compare(set.value);
      switch (set.op) {
        case "<":
          return c < 0;
        case "<=":
          return c <= 0;
        case ">":
          return c > 0;
        case ">=":
          return c >= 0;
      }
    }
    // eslint-disable-next-line no-fallthrough
    case "interval": {
      const aboveLo = set.loInclusive
        ? x.gte(set.lo)
        : x.gt(set.lo);
      const belowHi = set.hiInclusive ? x.lte(set.hi) : x.lt(set.hi);
      return aboveLo && belowHi;
    }
    case "union": {
      const lowPiece = set.loInclusive ? x.lte(set.lo) : x.lt(set.lo);
      const highPiece = set.hiInclusive ? x.gte(set.hi) : x.gt(set.hi);
      return lowPiece || highPiece;
    }
  }
}

/** Render a solution set to LaTeX in x. */
export function solutionSetLatex(set: SolutionSet, v = "x"): string {
  switch (set.kind) {
    case "all":
      return "\\text{all real numbers}";
    case "empty":
      return "\\text{no solution}";
    case "ray":
      return `${v} ${opLatex(set.op)} ${set.value.toLatex()}`;
    case "interval":
      return `${set.lo.toLatex()} ${opLatex(set.loInclusive ? "<=" : "<")} ${v} ${opLatex(set.hiInclusive ? "<=" : "<")} ${set.hi.toLatex()}`;
    case "union":
      return `${v} ${opLatex(set.loInclusive ? "<=" : "<")} ${set.lo.toLatex()} \\;\\text{or}\\; ${v} ${opLatex(set.hiInclusive ? ">=" : ">")} ${set.hi.toLatex()}`;
  }
}

export function opLatex(op: Op): string {
  switch (op) {
    case "<":
      return "<";
    case "<=":
      return "\\le";
    case ">":
      return ">";
    case ">=":
      return "\\ge";
  }
}

export function opText(op: Op): string {
  return op;
}

// --- Bridge to the answer/grading model ---------------------------------

import type { AnswerValue } from "./answer";

/**
 * Convert a SolutionSet into a gradable AnswerValue:
 *  - ray      -> SimpleInequality
 *  - interval -> CompoundInequality (AND)
 *  - union    -> CompoundInequality (OR)
 *  - all      -> special "infinitely-many"
 *  - empty    -> special "no-solution"
 */
export function solutionSetToAnswer(set: SolutionSet): AnswerValue {
  switch (set.kind) {
    case "all":
      return { kind: "special", value: "infinitely-many" };
    case "empty":
      return { kind: "special", value: "no-solution" };
    case "ray":
      return { kind: "inequality", op: set.op, value: set.value };
    case "interval":
      return {
        kind: "compound",
        connector: "and",
        lower: { value: set.lo, inclusive: set.loInclusive },
        upper: { value: set.hi, inclusive: set.hiInclusive },
      };
    case "union":
      return {
        kind: "compound",
        connector: "or",
        lower: { value: set.lo, inclusive: set.loInclusive },
        upper: { value: set.hi, inclusive: set.hiInclusive },
      };
  }
}
